// Practitioner-facing stats panel — questionnaire volume between two dates.
// One row per template used by this cabinet, with the number of responses
// received (completed_at within range) and pending (status "sent"/"scheduled",
// sent_at within range) between two inclusive dates. Admin-only in Settings.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChartBar, ArrowClockwise, CheckCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

// Convert a JS Date to YYYY-MM-DD for the <input type="date"> and API params.
const toIso = (d) => d.toISOString().slice(0, 10);

// Default range: rolling 30 days ending today (inclusive).
function defaultRange() {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 29);
  return { from: toIso(from), to: toIso(today) };
}

export default function StatsPanel() {
  const [range, setRange] = useState(defaultRange);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ received: 0, pending: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/stats/assignments-summary", {
        params: { date_from: range.from, date_to: range.to },
      });
      setRows(data.rows || []);
      setTotals(data.totals || { received: 0, pending: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les statistiques");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range.from, range.to]);

  const sorted = useMemo(() => [...rows].sort((a, b) => (b.received + b.pending) - (a.received + a.pending)), [rows]);

  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-[#E1E9F0]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1E3A5F]">
            <ChartBar size={20} weight="duotone" className="text-[#3A8FA5]" />
            Statistiques par questionnaire
          </CardTitle>
          <p className="text-[13px] text-[#5F758F] leading-relaxed mt-1">
            Détail par questionnaire du nombre de réponses reçues et d'envois en attente de réponse sur la période choisie.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Du</Label>
              <Input
                type="date"
                value={range.from}
                max={range.to}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                className="mt-2 bg-white border-[#E1E9F0]"
                data-testid="stats-date-from"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Au</Label>
              <Input
                type="date"
                value={range.to}
                min={range.from}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                className="mt-2 bg-white border-[#E1E9F0]"
                data-testid="stats-date-to"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="rounded-[10px] border-[#E1E9F0] text-[#5F758F] font-semibold"
                onClick={() => setRange(defaultRange())}
                disabled={loading}
                data-testid="stats-reset-range"
              >
                <ArrowClockwise size={14} className="mr-1.5" /> 30 derniers jours
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-[12px] bg-[#E6F1F5] border border-[#C7DEE6] px-4 py-3">
              <div className="flex items-center gap-1.5 text-[#164E5F] text-[11px] font-semibold uppercase tracking-widest">
                <CheckCircle size={12} weight="fill" /> Réponses reçues
              </div>
              <p className="text-3xl font-bold text-[#1E3A5F] mt-1" data-testid="stats-total-received">{totals.received}</p>
            </div>
            <div className="rounded-[12px] bg-[#FFF3D6] border border-[#F0DDA8] px-4 py-3">
              <div className="flex items-center gap-1.5 text-[#8A6900] text-[11px] font-semibold uppercase tracking-widest">
                <Clock size={12} weight="fill" /> En attente de réponse
              </div>
              <p className="text-3xl font-bold text-[#1E3A5F] mt-1" data-testid="stats-total-pending">{totals.pending}</p>
            </div>
          </div>

          <div className="rounded-[12px] border border-[#E1E9F0] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F7FB] text-[#5F758F] text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Questionnaire</th>
                  <th className="text-right px-4 py-3 font-semibold">Réponses reçues</th>
                  <th className="text-right px-4 py-3 font-semibold">En attente de réponse</th>
                  <th className="text-right px-4 py-3 font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[#5F758F]">Chargement…</td></tr>
                )}
                {!loading && sorted.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[#5F758F]">Aucun envoi sur la période sélectionnée.</td></tr>
                )}
                {!loading && sorted.map((r) => (
                  <tr key={r.template_id} className="border-t border-[#E1E9F0] hover:bg-[#F4F7FB]" data-testid={`stats-row-${r.template_id}`}>
                    <td className="px-4 py-3 text-[#1E3A5F] font-semibold">
                      {r.template_title}
                      {r.category && (
                        <span className="ml-2 text-[11px] font-normal text-[#5F758F]">· {r.category}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-[#164E5F] font-semibold" data-testid={`stats-received-${r.template_id}`}>{r.received}</td>
                    <td className="px-4 py-3 text-right text-[#8A6900] font-semibold" data-testid={`stats-pending-${r.template_id}`}>{r.pending}</td>
                    <td className="px-4 py-3 text-right text-[#5F758F]">{r.received + r.pending}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
